import { useCallback, useEffect, useRef, useState } from "react";

/**
 * A unified controller for mouse interactions on a target element.
 * Allows for mouse events on overlapping elements.
 * - Tracks element-relative coords, global coords, primary button state,
 *   modifiers, wheel deltas, pointerType, and insideElement.
 *
 * Options:
 *   target: React.RefObject<HTMLElement>  // required to compute element-relative coords
 *   listenOn: "window" | "document"       // event target; default "window"
 *   capture: boolean                       // capture phase for events; default false
 *   rafThrottle: boolean                   // rAF throttle move updates; default true
 *   wheel: "none" | "pixel" | "line" | "page" // report wheel deltas; default "pixel"
 *   preventDefaultOnWheel: boolean         // prevent default wheel when over target; default false
 */
//-----------------------------------------------------------------
export function useMouseController(options = {}) {
  //-----------------------------------------------------------------
  //#region [VALUES]

  const {
    target,
    listenOn    = "window",
    capture     = false,
    rafThrottle = true,
    wheel       = "pixel", // "none" | "pixel" | "line" | "page"
    preventDefaultOnWheel = false,
  } = options;

  // Internal refs
  const ref_frame          = useRef(null);
  const ref_last_event     = useRef(null);
  const ref_primary_down   = useRef(false);
  const ref_secondary_down = useRef(false);
  const ref_pointer_id     = useRef(null);
  const ref_inside         = useRef(null);
  
  const computeRelative = useCallback( _computeRelative , [target] );
  const publish         = useCallback( _publish         , [computeRelative] );
  const schedule        = useCallback( _schedule        , [publish, rafThrottle] );

  // State exposed to consumers
  const [state, setState] = useState(() => ({
    // Element-relative (logical CSS px)
    x: null,
    y: null,
    insideElement: null,

    // Buttons
    primaryDown   : false,
    secondaryDown : false,

    // Modifiers
    altKey   : false,
    ctrlKey  : false,
    metaKey  : false,
    shiftKey : false,
  }));

  //#endregion

  //#region [EFFECT]

  useEffect(() => {
    const event_target = listenOn === "document" ? document : window;

    //-----------------------------------------------------------------
    const onMouseDown = (event) => {
      //-----------------------------------------------------------------
      ref_last_event    .current = event;
      ref_primary_down  .current = event.button === 0;
      ref_secondary_down.current = event.button === 2;
      schedule();
    };

    //-----------------------------------------------------------------
    const onMouseMove = (event) => {
      //-----------------------------------------------------------------
      ref_last_event.current = event;
      schedule();
    };

    //-----------------------------------------------------------------
    const onMouseUp = (event) => {
      //-----------------------------------------------------------------
      ref_last_event    .current = event;
      ref_primary_down  .current = false;
      ref_secondary_down.current = false;

      schedule();
    };

    //-----------------------------------------------------------------
    const onContextMenu = (event) => {
    //-----------------------------------------------------------------
      if( !state.insideElement ) return;

      event.preventDefault();
      return false;
    }

    // Wheel handling (optional)
    //-----------------------------------------------------------------
    const onWheel = (event) => {
      //-----------------------------------------------------------------
      if (preventDefaultOnWheel && target?.current) {
        // Only prevent if the wheel is over target to keep page scroll normal elsewhere
        const rect = target.current.getBoundingClientRect();
        const over =
          event.clientX >= rect.left   &&
          event.clientX <= rect.right  &&
          event.clientY >= rect.top    &&
          event.clientY <= rect.bottom;
        if (over) event.preventDefault();
      }

      if (wheel === "none") return;

      // Normalize deltas lightly; consumers decide semantics
      // Keep raw deltas but tag with chosen mode
      setState((prev) => ({
        ...prev,
        wheelDX   :   event.deltaX   ,
        wheelDY   :   event.deltaY   ,
        wheelDZ   :   event.deltaZ   ,
        wheelMode :   wheel          ,
        altKey    : !!event.altKey   ,
        ctrlKey   : !!event.ctrlKey  ,
        metaKey   : !!event.metaKey  ,
        shiftKey  : !!event.shiftKey ,
      }));

      // Also update position snapshot for consumers that zoom around cursor
      ref_last_event.current = event;
      schedule();
    };

    // Attach listeners
    event_target.addEventListener("mousedown"   , onMouseDown    , { capture });
    event_target.addEventListener("mousemove"   , onMouseMove    , { capture });
    event_target.addEventListener("mouseup"     , onMouseUp      , { capture });
    event_target.addEventListener("contextmenu" , onContextMenu  , { capture });

    // Wheel is separate (pointer events don't cover it)
    if (wheel !== "none") {
      event_target.addEventListener("wheel", onWheel, { capture, passive: !preventDefaultOnWheel });
    }

    // Recompute on layout changes (resize/scroll)
    //-----------------------------------------------------------------
    const onLayout = () => {
      //-----------------------------------------------------------------
      if (!ref_last_event.current) return;
      schedule();
    };

    window.addEventListener("resize", onLayout);
    // capture scroll on ancestors, not just window
    window.addEventListener("scroll", onLayout, true);

    return () => {
      event_target.removeEventListener("mousedown"   , onMouseDown   , { capture });
      event_target.removeEventListener("mousemove"   , onMouseMove   , { capture });
      event_target.removeEventListener("mouseup"     , onMouseUp     , { capture });
      event_target.removeEventListener("contextmenu" , onContextMenu , { capture });

      if (wheel !== "none") {
        event_target.removeEventListener("wheel", onWheel, { capture });
      }

      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);

      if (ref_frame.current != null) {
        cancelAnimationFrame(ref_frame.current);
        ref_frame.current = null;
      }

      ref_last_event    .current = null;
      ref_primary_down  .current = false;
      ref_secondary_down.current = false;
      ref_pointer_id    .current = null;
    };
  }, [
    listenOn,
    capture,
    rafThrottle,
    schedule,
    target,
    wheel,
    state.insideElement,
    preventDefaultOnWheel,
  ]);

  //#endregion

  //#region [METHOD]

  // Compute element-relative coordinates
  //-----------------------------------------------------------------
  function _computeRelative(clientX, clientY) {
    //-----------------------------------------------------------------
    const element = target?.current;
    
    if (!element) {
      return { x: null, y: null, inside: null };
    }

    const rect = element.getBoundingClientRect();
    const x = parseInt(clientX - rect.left);
    const y = parseInt(clientY - rect.top);
    
    const inside =
      clientX >= rect.left   &&
      clientX <= rect.right  &&
      clientY >= rect.top    &&
      clientY <= rect.bottom;

    return { x, y, inside };
  }

  //-----------------------------------------------------------------
  function _publish() {
    //-----------------------------------------------------------------
    const event = ref_last_event.current;
    if ( !event ) return;

    const clientX = "clientX" in event ? event.clientX : null;
    const clientY = "clientY" in event ? event.clientY : null;

    const has_position = clientX != null && clientY != null;
    const position     = has_position
        ? computeRelative(clientX, clientY)
        : { x: null, y: null, inside: null };

    const buttonsMask = "buttons" in event ? event.buttons : 0;
    const primaryMaskDown   = (buttonsMask & 1) === 1;
    const secondaryMaskDown = (buttonsMask & 2) === 2;
    const primaryDown   = ref_primary_down.current   || primaryMaskDown;
    const secondaryDown = ref_secondary_down.current || secondaryMaskDown;

    // if( position.inside === false ) return;
    setState((prev) => {
      const next = {
        x             : position.inside ? position.x : prev.x,
        y             : position.inside ? position.y : prev.y,
        insideElement : position.inside,
        primaryDown   : position.inside ? primaryDown      : false,
        secondaryDown : position.inside ? secondaryDown    : false,
        altKey        : position.inside ? !!event.altKey   : prev.altKey,
        ctrlKey       : position.inside ? !!event.ctrlKey  : prev.ctrlKey,
        metaKey       : position.inside ? !!event.metaKey  : prev.metaKey,
        shiftKey      : position.inside ? !!event.shiftKey : prev.shiftKey,
      };

      // Shallow equal check to reduce renders
      if (
        next.x             === prev.x             &&
        next.y             === prev.y             &&
        next.insideElement === prev.insideElement &&
        next.primaryDown   === prev.primaryDown   &&
        next.secondaryDown === prev.secondaryDown &&
        next.altKey        === prev.altKey        &&
        next.ctrlKey       === prev.ctrlKey       &&
        next.metaKey       === prev.metaKey       &&
        next.shiftKey      === prev.shiftKey
      ) {
        return prev;
      }
      return next;
    });
  }

  //-----------------------------------------------------------------
  function _schedule() {
    //-----------------------------------------------------------------
    if (!rafThrottle) {
      publish();
      return;
    }

    if (ref_frame.current == null) {
      ref_frame.current = window.requestAnimationFrame( () => {
        ref_frame.current = null;
        publish();
      });
    }
  }

  //#endregion

  return state;
}